import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {
  Chip,
  EmptyStatus,
  EmptyStatusAction,
  Separator,
  showNotification,
} from 'platform/components';
import {
  Box,
  getValueByDevice,
  Heading,
  HStack,
  Show,
  Space,
  Text,
  useDevice,
  VStack,
  Grid,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {filter, isNil, isNotNil, map} from 'ramda';
import {isNotNilOrEmpty, isString, isTrue} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useGetVehicleQuery,
  useSetFeatureAsHighlightedMutation,
  useSetFeatureAsNotHighlightedMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  FeatureSubCategory,
  groupFeaturesBySubCategory,
  handleApiError,
  usePermissions,
} from '@dms/shared';
import {VehicleCatalogueEnumOption} from '@dms/teas';

import {convertStringToCamelCase, sortByAsciiNumberLast} from 'shared/web';

interface FeaturesDetailProps {
  categories: VehicleCatalogueEnumOption[];
  features: (VehicleCatalogueEnumOption & {isHighlighted?: boolean})[];
  addFeaturesAction?: EmptyStatusAction;
  vehicleId: string;
}

export const FeaturesDetail = ({
  categories,
  features,
  addFeaturesAction,
  vehicleId,
}: FeaturesDetailProps) => {
  const device = useDevice();
  const {data: vehicle} = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);
  const [setFeatureAsHighlighted] = useSetFeatureAsHighlightedMutation();
  const [setFeatureAsNotHighlighted] = useSetFeatureAsNotHighlightedMutation();

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
      recordId: vehicle?.id!,
    },
    {
      skip: isNil(vehicle),
    }
  );

  const [canUpdateFeature] = usePermissions({
    permissionKeys: ['updateFeature'],
    scopes: {
      updateFeature: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const isSubCategoriesEnabled = isFeatureEnabled(featureFlags.SALES_FEATURE_SUBCATEGORIES);
  const subCategories = getSubCategories(categories);

  const nonEmptyCategories = categories?.filter((item) =>
    features.some((feature) => feature.categories?.includes(item.value))
  );

  const handleSetFeature = (featureKey: string, value: boolean) => {
    if (!canUpdateFeature) {
      return;
    }
    if (isNil(vehicle)) {
      showNotification.error();
      return;
    }

    const clickAction = value ? setFeatureAsNotHighlighted : setFeatureAsHighlighted;

    clickAction({vehicleId: vehicle.id, featureKey}).unwrap().catch(handleApiError);
  };

  const renderFeature = (item: FeaturesDetailProps['features'][number]) => (
    <Box key={item.value}>
      <Chip
        onChange={(value) => handleSetFeature(item.value, value)}
        value={isTrue(item.isHighlighted)}
        label={item.label}
        data-testid={testIds.vehicles.features(convertStringToCamelCase(item.value))}
        size="small"
        variant="ghost"
      />
    </Box>
  );

  const getSortedFeaturesInCategories = (categoryKeys: string[]) =>
    filter(
      (feature) => feature?.categories?.some((key) => isKeyIn(categoryKeys, key)) ?? false,
      features
    ).sort((a, b) => sortByAsciiNumberLast(a.label, b.label));

  const renderFeaturesInCategory = (categoryKey: string) => {
    const mappedList = map(renderFeature, getSortedFeaturesInCategories([categoryKey]));

    return (
      <Show when={isNotNilOrEmpty(mappedList)}>
        {match(isSubCategoriesEnabled)
          .with(true, () => (
            <HStack spacing={1} wrap>
              {mappedList}
            </HStack>
          ))
          .otherwise(() => (
            <Grid columns={getValueByDevice(device, 1, 1, 2, 3, 3)} spacing={2}>
              {mappedList}
            </Grid>
          ))}
      </Show>
    );
  };

  const renderFeaturesBySubCategory = (category: VehicleCatalogueEnumOption) => {
    // Features assigned only to a child category or any of its descendants (without sub_category)
    // fall into that child's group
    const childGroups = (category.children ?? []).map((child) => ({
      key: child.value,
      keys: getCategoryKeys(child),
    }));
    const groups = groupFeaturesBySubCategory({
      items: getSortedFeaturesInCategories([
        category.value,
        ...childGroups.flatMap((child) => child.keys),
      ]),
      categoryKey: category.value,
      subCategories,
      getSubCategory: (feature) =>
        feature.subCategory ??
        childGroups.find((child) => feature.categories?.some((key) => isKeyIn(child.keys, key)))
          ?.key,
    });

    if (groups.every((group) => group.isOther)) {
      return renderFeaturesInCategory(category.value);
    }

    return (
      <VStack>
        {groups.map((group, index) => (
          <Box
            key={group.key}
            paddingTop={index === 0 ? 0 : 3}
            paddingBottom={3}
            borderTop={index === 0 ? undefined : '1px solid'}
            borderColor="palettes.neutral.30.100"
            data-testid={testIds.vehicles.features(
              `featuresDetail-${convertStringToCamelCase(category.value)}-${convertStringToCamelCase(group.key)}`
            )}
          >
            <VStack spacing={1}>
              <Box paddingLeft={2}>
                <Heading size={6} color="secondary">
                  {group.label ?? i18n.t('entity.vehicle.labels.otherLabel')}
                </Heading>
              </Box>
              <HStack spacing={1} wrap>
                {map(renderFeature, group.items)}
              </HStack>
            </VStack>
          </Box>
        ))}
      </VStack>
    );
  };

  const renderCategory = (category: VehicleCatalogueEnumOption, level = 0): ReactNode => (
    <>
      <>
        {level === 0 ? (
          <Box>
            <Heading
              size={4}
              data-testid={testIds.vehicles.features(
                `featuresDetail-${convertStringToCamelCase(category.value)}`
              )}
            >
              {category.label}
            </Heading>
          </Box>
        ) : (
          <Box paddingTop={8}>
            <Heading
              size={5}
              data-testid={testIds.vehicles.features(
                `featuresDetail-${convertStringToCamelCase(category.value)}`
              )}
            >
              {category.label}
            </Heading>
          </Box>
        )}
        <Box paddingTop={4} />
        {match(isSubCategoriesEnabled)
          .with(true, () => renderFeaturesBySubCategory(category))
          .otherwise(() => renderFeaturesInCategory(category.value))}
      </>
      {!isSubCategoriesEnabled &&
        !!category.children?.length &&
        category.children.map((child, index) => (
          <Show
            key={index} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
            when={features.some((feature) => feature.categories?.includes(child.value))}
          >
            <div>{renderCategory(child, level + 1)}</div>
          </Show>
        ))}
    </>
  );

  return (
    <div>
      <Show when={isNotNilOrEmpty(features)}>
        <Text color="secondary" size="small">
          {i18n.t('entity.vehicle.labels.clickOnFeatureAlert')}
        </Text>
        <Space vertical={2} />
      </Show>
      <Space vertical={2} />
      {features.length ? (
        nonEmptyCategories.map((category, i) => (
          <div
            key={category.value}
            data-testid={testIds.vehicles.features(convertStringToCamelCase(category.value))}
          >
            {renderCategory(category)}
            <Show when={i !== nonEmptyCategories.length - 1}>
              <Separator />
            </Show>
          </div>
        ))
      ) : (
        <Box>
          <EmptyStatus
            action={{
              title: i18n.t('entity.vehicle.actions.addFeatures'),
              leftIcon: 'content/add_circle',
              ...addFeaturesAction,
            }}
            headline={i18n.t('entity.vehicle.labels.noFeaturesToShow')}
            data-testid={testIds.vehicles.features('featuresDetail-noFeaturesToShow')}
          />
        </Box>
      )}
      <Show when={isNotNil(vehicle) && isNotNilOrEmpty(vehicle.featuresByManufacturer)}>
        <Separator />
        <VStack spacing={2}>
          <Heading size={4}>{i18n.t('entity.vehicle.labels.featuresByManufacturer')}</Heading>
          <Box paddingLeft={2}>
            <Text size="small" preWrap>
              {vehicle?.featuresByManufacturer}
            </Text>
          </Box>
        </VStack>
      </Show>
    </div>
  );
};

function isKeyIn(
  keys: string[],
  key: NonNullable<VehicleCatalogueEnumOption['categories']>[number]
) {
  return isString(key) && keys.includes(key);
}

function getCategoryKeys(category: VehicleCatalogueEnumOption): string[] {
  return [category.value, ...(category.children ?? []).flatMap(getCategoryKeys)];
}

function getSubCategories(categories: VehicleCatalogueEnumOption[]): FeatureSubCategory[] {
  return categories.flatMap((category) =>
    (category.children ?? []).map((child) => ({
      key: child.value,
      label: child.label || child.value,
      parent: category.value,
    }))
  );
}
